import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import MorningBriefingModal from './MorningBriefingModal';
import { useOpsContext } from './useOpsContext';
import {
    useOpsBriefingAccess,
    useOpsBriefingQuery,
    useOpsOnboardingQuery,
    useOpsCoachSettlingQuery,
    useOpsCoachOnboardingQuery,
} from '../../services/useOpsBriefingService';
import { resolveSetupTasks } from './setupTasks';
import { readHiddenSetupTasks } from './setupTaskState';
import { usePayrollAuthContext } from '../../context/PayrollAuthContext';
import { OPEN_MORNING_BRIEFING_EVENT } from './morningBriefingEvent';
import { setMorningBriefingOpen } from './morningBriefingOpenStore';

/**
 * 아침 브리핑 팝업 게이트.
 *
 * ⚠️ 이 컴포넌트는 PrivateRoutes 에 붙어 있어 **모든 화면에서 돌아갑니다.**
 * 게이트가 없으면 배포하는 순간 전 센터 원장에게 매일 아침 팝업이 뜹니다.
 * 메뉴를 숨기는 것과는 차원이 다릅니다. 노출 조건을 반드시 여기서 막습니다.
 *
 * 노출 조건
 *   · 슈퍼관리자 센터에서만 (아직 검증 중인 기능)
 *   · 오늘 브리핑에 실제로 항목이 있을 때만 — 빈 팝업은 방해일 뿐이다
 *
 * 정책
 *   · 「오늘은 그만 보기」를 누르기 전까지는 **화면을 옮길 때마다 계속** 뜬다.
 *     매일 아침 반드시 확인해야 하는 내용이라, 한 번 닫았다고 그날 안 보이면 놓친다.
 *   · 「오늘은 그만 보기」를 누르면 그날은 다시 뜨지 않는다. (날짜를 localStorage 에 기록)
 *   · 브리핑 화면(/opsBriefing) 안에서는 뜨지 않는다. 이미 보고 있기 때문.
 *   · 브리핑 생성 시각(08:04) 이전에는 뜨지 않는다 — 이중 안전장치.
 *
 * 같은 화면에서 닫았을 때 즉시 다시 열리지 않도록, 경로가 바뀔 때만 재판단한다.
 */

const STORAGE_KEY = 'opsBriefingModalMutedAt';
/*
 * ─────────────────────────────────────────────────────────────
 * 🔴 시각 게이트는 **없앴다** (2026-09-02, 생성 배치 자정 이동)
 * ─────────────────────────────────────────────────────────────
 * 예전에는 `BRIEFING_READY_HOUR = 8` 로 8시 전에는 팝업을 막았다. 생성이
 * 08:04 이라 그전에는 어차피 데이터가 없었고, 시각 검사는 이중 안전장치였다.
 *
 * 생성이 **자정으로** 옮겨지면서 그 전제가 뒤집혔다. 새벽에 접속한 원장은
 * 그날 브리핑이 **이미 만들어져 있는데도** 못 보게 된다 — 안전장치가
 * 「일부러 감추는 장치」로 변한다.
 *
 * 그래서 판단은 **데이터 존재 여부 하나로만** 한다(`hasWork`). 시계를 보고
 * 정하지 않는다 — 배치 시각이 또 바뀌어도 이 화면은 안 고쳐도 된다.
 *
 * ⚠️ 「그날 문서가 없다」와 「항목이 0건이다」를 가르는 판단은 **그대로 둔다**
 *    (오늘의 시그널 화면의 `briefingNotGenerated`). 배치가 실패해 문서가
 *    없는 날은 여전히 있을 수 있고, 그건 시각과 무관한 사실이다.
 */
/*
 * 이벤트 이름은 morningBriefingEvent.ts 에 있다.
 * 이름만 필요한 화면이 이 파일을 import 하면 잠금 컨텍스트까지 딸려오기 때문이다.
 * 예전 import 경로를 쓰는 곳이 있을 수 있어 여기서도 그대로 내보낸다.
 */
export { OPEN_MORNING_BRIEFING_EVENT } from './morningBriefingEvent';

const isMutedToday = () => {
    try {
        return window.localStorage.getItem(STORAGE_KEY) === dayjs().format('YYYY-MM-DD');
    } catch (e) {
        // 저장소 접근 불가(사파리 프라이빗 등) — 음소거를 기억하지 못할 뿐이다
        return false;
    }
};

type GateProps = {
    /**
     * 업데이트 소식 팝업이 떠 있는 동안 **열리지 않는다** (2026-09-02).
     *
     * 🔴 로그인 직후에는 업데이트 소식 팝업과 이 팝업이 **같은 시점에** 뜬다. 겹치면
     *    원장님은 무엇을 닫은 건지 모르고, 뒤에 있던 팝업이 「닫았는데 또 뜬다」로
     *    읽힌다. 업데이트 소식을 먼저 보이고, 닫으면 그때 이 팝업이 뜬다.
     *
     * ⚠️ **막기만 하고 기억하지는 않는다.** 잠긴 동안 열지 않을 뿐이라,
     *    풀리면 같은 규칙(하루 1회·경로별 닫기)이 그대로 적용된다.
     */
    suspended?: boolean;
};

const MorningBriefingGate = ({ suspended = false }: GateProps) => {
    const location = useLocation();
    const ctx = useOpsContext();
    const [open, setOpen] = useState(false);

    /*
     * 노출 게이트.
     *
     * 예전에는 team-feature-status 를 봤습니다. 그 값은 "프랜차이즈 산하인가"라서
     * 전체 센터의 약 **절반**이 해당됩니다 — 그대로 뒀다면 배포하는 순간
     * 절반의 센터 원장에게 매일 아침 팝업이 떴습니다.
     *
     * allowed 는 서버가 명시적으로 열렸다고 답했을 때만 true 입니다.
     * 로딩 중·조회 실패는 전부 false 입니다 (fail-closed, accessGate.ts 참고).
     * 이 컴포넌트는 모든 화면에서 도는데다 팝업은 되돌릴 수 없으니,
     * "아직 모름"을 열림으로 취급하면 안 됩니다.
     */
    const { allowed } = useOpsBriefingAccess(ctx);
    /*
     * 브리핑 탭이 잠겨 있으면 팝업도 뜨지 않는다.
     *
     * 탭은 2차 비밀번호로 잠가 두고 팝업이 같은 회원 이름·수치를 띄우면
     * 잠근 의미가 없다 (자리를 비운 사이 PC 앞에 누가 앉든 그대로 보인다).
     * 여기서 비밀번호를 묻지는 않는다 — 팝업은 원장이 부른 것이 아니라 저절로 뜨는 것이라,
     * 자동으로 인증 창을 띄우면 매일 아침 모든 화면에서 비밀번호를 묻게 된다.
     * 잠겨 있으면 **조용히 띄우지 않고**, 탭에 들어가서 인증하면 그때 본다.
     */
    const { isSectionLocked, isSectionUnlocked } = usePayrollAuthContext();
    const briefingLocked = isSectionLocked('opsBriefing') && !isSectionUnlocked('opsBriefing');

    /*
     * 목 데이터를 직접 import 하고 있었습니다.
     * 그러면 목 모드 플래그와 상관없이 **항상 가짜 브리핑**이 뜹니다 —
     * 전 센터 원장이 매일 아침 없는 회원 이름을 보게 됩니다.
     * 다른 화면과 같은 조회 훅을 씁니다. 목/실서버 판단은 api 레이어가 합니다.
     */
    /*
     * ⚠️ 세 번째 인자(allowed)를 빼지 마세요.
     * 브리핑 조회는 서버 노출 게이트 **안**이라 닫힌 센터에서 부르면 404 입니다.
     * 이 컴포넌트는 모든 화면에서 돌기 때문에, 게이트를 빼면 닫힌 센터의 콘솔에
     * 화면을 옮길 때마다 404 가 계속 찍힙니다.
     */
    const today = dayjs().format('YYYY-MM-DD');
    const { data } = useOpsBriefingQuery(ctx, today, allowed);
    const items = data?.items ?? [];
    /*
     * ─────────────────────────────────────────────────────────────
     * 🔴 신규 센터에는 회원 항목이 0건이라 팝업이 안 떴다 (2026-09-01 QA23)
     * ─────────────────────────────────────────────────────────────
     * 강현구님 센터에서 실측: 브리핑 API 200, **items 0건**. 회원 항목이 없으니
     * 아래 게이트가 그대로 return 했고, 그 센터 원장님은 아침 팝업을 한 번도
     * 본 적이 없다. 그런데 그 센터에도 **오늘 할 일은 있다** — 강사 연결·회원
     * 배정 같은 세팅 과제다. 오늘의 브리핑 화면은 그걸 카드로 세우고 있었는데
     * 팝업만 못 보고 있었다.
     *
     * 오늘의 브리핑과 **같은 함수**로 뽑는다. 두 곳이 다르게 세면 팝업이
     * 「1건」이라 하고 화면에는 2장이 뜬다.
     *
     * ⚠️ 두 조회 모두 실패를 null 로 삼킨다(api.optional). 그러면 과제가 0건이라
     *    예전과 똑같이 동작한다 — 없는 팝업을 만들지 않는다.
     */
    const { data: onboarding } = useOpsOnboardingQuery(ctx, allowed);
    const { data: coachSettling } = useOpsCoachSettlingQuery(ctx, allowed);
    /*
     * 신규 센터의 팝업은 **강사 카드**를 말한다 (2026-09-01 대표 시안).
     * 없으면(배포 전·404) null 이라 아래가 전부 예전 동작으로 떨어진다.
     */
    const { data: coachOnboarding } = useOpsCoachOnboardingQuery(ctx, allowed);
    /*
     * 🔴 신규 센터인가 — **화면과 같은 판정**을 쓴다 (서버가 준 세팅 단계).
     *    화면은 강사 카드를 세우는데 팝업만 예전 축 카드를 세면 두 화면이
     *    다른 「오늘 할 일」을 말한다 (QA34 에서 겪은 그 사고다).
     */
    /*
     * ⚠️ 여기서는 **후보만** 고른다. 실제로 목록을 대체할지는
     *    `MorningBriefingModal` 이 화면과 같은 계산기(`composeBriefing` 의
     *    `centerStage`)로 정한다 — 이 컴포넌트에는 편성 결과가 없다.
     *    2026-09-02 QA36 전에는 이 조건 하나가 곧 대체 여부였고,
     *    「세팅 미졸업」이 정착 센터에서도 늘 참이라 팝업이 통째로 바뀌었다.
     */
    const onboardingCoaches =
        coachOnboarding && coachOnboarding.setupSteps.done < coachOnboarding.setupSteps.total
            ? coachOnboarding.coaches
            : null;
    /*
     * 가린 과제는 팝업에서도 빠진다 (2026-09-01 QA29) — 화면에서 안 보이는 것을
     * 팝업이 세면 「N건」이 화면 카드 수와 어긋난다.
     */
    const setupTasks = useMemo(() => {
        const hidden = readHiddenSetupTasks();
        return resolveSetupTasks(onboarding, coachSettling).filter((t) => !hidden.has(t.id));
    }, [onboarding, coachSettling]);
    /** 팝업을 띄울 이유가 하나라도 있는가 */
    const hasWork = items.length > 0 || setupTasks.length > 0 || (onboardingCoaches?.length ?? 0) > 0;
    /** 같은 경로에서 닫은 직후 다시 열리는 것을 막는다 */
    const closedAtPathRef = useRef<string | null>(null);

    useEffect(() => {
        const path = location.pathname;

        // 브리핑 화면에서는 중복 노출하지 않는다
        if (path.startsWith('/opsBriefing')) return;
        // 업데이트 소식 팝업이 떠 있다 — 닫으면 이 effect 가 다시 돌아 그때 뜬다
        if (suspended) return;
        // 방금 이 경로에서 닫았으면 다시 열지 않는다
        if (closedAtPathRef.current === path) return;
        if (isMutedToday()) return;
        if (!allowed) return;
        /*
         * 🔴 잠겨 있어도 **띄운다.** 대신 내용을 가린다 (MorningBriefingModal 의 locked).
         *
         * 예전에는 여기서 조용히 return 했다. 그런데 브리핑 탭의 기본값이 잠금이라
         * (lockedSections.DEFAULT_LOCKED_CLIENT_SECTIONS), 잠금 설정을 한 번도
         * 만진 적 없는 브라우저 — 즉 **첫 로그인** — 에서는 팝업이 아예 안 떴다.
         * 2026-08-28 QA 1 이 그 증상이다.
         *
         * 탭 기본 잠금도, 첫 로그인 팝업도 둘 다 대표 결정이라 어느 한쪽을
         * 무력화할 수 없다. 그래서 「가리고 띄운다」로 둘을 다 지킨다 —
         * 건수만 알리고 회원 이름·수치·금액은 하나도 안 그린다.
         */
        // 오늘 할 일이 없으면 띄우지 않는다. 빈 팝업은 방해일 뿐이다
        if (!hasWork) return;

        setOpen(true);
    }, [location.pathname, allowed, briefingLocked, hasWork, suspended]);

    // 수동 열기 (브리핑 화면의 "아침 팝업 다시 보기")
    useEffect(() => {
        const handler = () => setOpen(true);
        window.addEventListener(OPEN_MORNING_BRIEFING_EVENT, handler);
        return () => window.removeEventListener(OPEN_MORNING_BRIEFING_EVENT, handler);
    }, []);

    /** 일단 닫기 — 다른 화면으로 이동하면 다시 뜬다 */
    const handleDismiss = () => {
        closedAtPathRef.current = location.pathname;
        setOpen(false);
    };

    /** 오늘은 그만 보기 — 오늘 하루 뜨지 않는다 */
    const handleMuteToday = () => {
        try {
            window.localStorage.setItem(STORAGE_KEY, dayjs().format('YYYY-MM-DD'));
        } catch (e) {
            // 저장 실패는 무시 — 다음 이동에 다시 뜰 뿐이다
        }
        closedAtPathRef.current = location.pathname;
        setOpen(false);
    };

    // 화면 안쪽의 다른 안내 팝업이 이 팝업과 겹치지 않게 떠 있는지 알린다(morningBriefingOpenStore).
    const isShown = open && allowed && hasWork && !suspended;
    useEffect(() => {
        setMorningBriefingOpen(isShown);
    }, [isShown]);
    useEffect(() => () => setMorningBriefingOpen(false), []);

    /*
     * 잠금은 더 이상 **열리는지**를 가르지 않고 **무엇을 보여줄지**를 가른다.
     * 수동 열기(브리핑 화면의 「아침 팝업 다시 보기」)도 같은 규칙을 탄다 —
     * 그 화면은 이미 인증을 지나온 곳이라 실제로는 잠금이 풀려 있다.
     */
    /* `suspended` 는 여기서도 본다 — 수동 열기 이벤트가 effect 를 우회하기 때문이다 */
    if (!isShown) return null;
    return (
        <MorningBriefingModal
            items={items}
            /*
             * 🔴 **거른 건수가 아니라 목록 원본을 넘긴다** (2026-09-01 QA34).
             *
             * 예전에는 `setupTasks.length` 를 넘겼다 — `resolveSetupTasks` 의
             * **날것**이다. QA29 가 이 함수를 「미완료 축 전부 + 막힌 강사마다
             * 한 장」으로 넓히면서 정착 센터에서 열두 장까지 나왔고, 팝업이
             * 「오늘 챙길 일 17건」이라고 말했다. 정작 화면은 다섯 건이었다.
             *
             * 자리 규칙(신규/과도기/정착)을 아는 것은 `composeBriefing` 뿐이다.
             * 그러니 세는 일을 팝업이 하지 말고, **화면과 같은 함수에 같은 것을
             * 먹여서** 같은 답이 나오게 한다.
             */
            setupTasks={setupTasks}
            /* 신규 센터면 이쪽이 목록이 된다 — 화면과 같은 것을 센다 */
            onboardingCoaches={onboardingCoaches}
            onDismiss={handleDismiss}
            onMuteToday={handleMuteToday}
            locked={briefingLocked}
        />
    );
};

export default MorningBriefingGate;
